import {BehaviorSubject, Subject, merge, shareReplay, combineLatest} from 'rxjs';
import {filter, map, skipUntil, startWith, switchMap} from 'rxjs/operators';
import {mc} from '@/core';
import {userInfo$} from '@/store/user';
import {kycApi} from '@/api/kyc';
import {browser$} from '@/streams/browser';
import {afterLogin$, isLogin$} from '@/streams/auth';
import {userApi} from '@/api';

export const loadKycInfo$ = new Subject();
export const kycInfo$ = new BehaviorSubject(createInitKycInfo());
export const canVerifyKyc$ = new BehaviorSubject(false);
export const initKycSummary$ = new BehaviorSubject(true);
export const kycSummary$ = new BehaviorSubject({basicKycStatus: '', basicKycInfoBo: {}, advanceKycStatus: '', advanceKycInfoBo: {}, limitCheck: false, kycFlag: 0});

merge(afterLogin$, loadKycInfo$)
  .pipe(switchMap(() => kycApi.getSummary()))
  .subscribe(({data, error}) => {
    if (data) {
      kycInfo$.next(data);
    }
    if (error) {
      kycInfo$.next(createInitKycInfo());
      mc.send('toast', 'error', error);
    }
  });

kycInfo$
  .pipe(
    skipUntil(browser$),
    filter((kycInfo) => kycInfo && !kycInfo.isInit && ['Init', 'Unverified'].indexOf(kycInfo.kycStatus) > -1),
    switchMap(() => kycApi.checkEnable(userInfo$.getValue().userId))
  )
  .subscribe(({data, error}) => {
    if (data) {
      canVerifyKyc$.next(data.enableCheck);
    }
    if (error) {
      canVerifyKyc$.next(false);
    }
  });

export const queryKycRecommend$ = new Subject();
export const kycRecommend$ = queryKycRecommend$.pipe(
  switchMap(() => getKycRecommend()),
  startWith({recommendBasicKyc: true, recommendAdvanceKyc: true, loading: true}),
  shareReplay(1)
);

export const userProfile$ = isLogin$.pipe(
  filter(Boolean),
  switchMap(() => getUserProfile()),
  startWith({registerRegionCode: '', loading: true}),
  shareReplay(1)
);

export const is_JP_KR$ = userProfile$.pipe(
  filter((pro) => !pro.loading && !!pro?.authUser?.userId),
  map((pro) => {
    return ['JP', 'KR'].includes(pro?.registerRegionCode?.toUpperCase());
  }),
  startWith(false),
  shareReplay(1)
);

export const isNewUser$ = kycSummary$.pipe(
  map((info) => info?.kycFlag === 1),
  shareReplay(1)
);

export const showKYCGuide$ = combineLatest([isNewUser$, is_JP_KR$, userProfile$, kycSummary$]).pipe(
  map(([isNew, isJPKR, profile, kycSummary]) => {
    if (isJPKR) {
      return false;
    }
    if (!!profile?.kolWhitelistChannel) {
      return false;
    }
    if (!isNew) {
      return false;
    }
    return kycSummary?.advanceKycStatus !== 'Verified';
  }),
  shareReplay(1)
);

// 特定渠道用户
export const isKolWhiteListChannel$ = userProfile$.pipe(
  filter((pro) => !pro.loading && !!pro?.authUser?.userId),
  map((pro) => !!pro?.kolWhitelistChannel),
  startWith(false),
  shareReplay(1)
);

async function getKycRecommend() {
  const {data} = await kycApi.kycRecommend();
  if (data) {
    return data;
  }
  return {recommendBasicKyc: true, recommendAdvanceKyc: true};
}

function createInitKycInfo() {
  return {
    kycStatus: 'Init',
    country: '-',
    fullName: '-',
    idNumber: '-',
    isInit: true,
  };
}

export async function getAllKycSummary() {
  const {data, error} = await kycApi.getAllKycSummary();
  if (data) {
    initKycSummary$.next(false);
    kycSummary$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return data || {};
}

export async function getUserProfile() {
  const {data} = await userApi.getUserProfile();
  if (data) {
    return data;
  }
  return {registerRegionCode: ''};
}

export function checkKycVerified(kycInfo) {
  if (kycInfo.advanceKycStatus === 'Verified' || kycInfo.basicKycStatus === 'Verified') {
    return true;
  }
  return false;
}
